import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, AlertCircle, Gift, ArrowLeft, Mail, User, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { referralAPI } from '../services/api';
import api from '../services/api';

export const Auth = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'login';
  const refCode = searchParams.get('ref') || '';
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState(refCode);
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    age: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    user_type: 'normal_user',  // "normal_user" or "doctor"
    medical_license: '',
    specialization: '',
    hospital_affiliation: '',
    consent_data_usage: false,
    consent_chat_moderation: false,
    consent_emergency_disclaimer: false,
  });

  // Validate referral code when it changes
  useEffect(() => {
    const validateReferral = async () => {
      if (referralCode && referralCode.length >= 4) {
        try {
          const response = await referralAPI.validateCode(referralCode);
          setReferralValid(response.data.valid);
          setReferralMessage(response.data.message);
        } catch {
          setReferralValid(false);
          setReferralMessage('Invalid referral code');
        }
      } else {
        setReferralValid(null);
        setReferralMessage('');
      }
    };
    
    const timer = setTimeout(validateReferral, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        // Redirect based on user type
        if (result?.user?.user_type === 'doctor') {
          navigate('/doctor');
        } else {
          navigate('/dashboard');
        }
      } else {
        if (!formData.consent_data_usage || !formData.consent_chat_moderation || !formData.consent_emergency_disclaimer) {
          setError('Please accept all consents to continue');
          setLoading(false);
          return;
        }
        const result = await register({
          ...formData,
          age: parseInt(formData.age)
        });
        
        // Apply referral code after registration if valid (only for normal users)
        if (referralCode && referralValid && formData.user_type === 'normal_user') {
          try {
            await referralAPI.applyCode(referralCode);
            toast.success('Referral code applied! Your referrer will earn credits when you subscribe.');
          } catch (refErr) {
            console.log('Referral apply failed:', refErr);
            // Don't block registration if referral fails
          }
        }
        
        // Show appropriate message based on user type
        if (formData.user_type === 'doctor') {
          toast.success('Account created! Your doctor account is pending admin approval.');
          navigate('/doctor');
        } else {
          const message = result.user?.message || 'Account created! Please verify your email.';
          toast.success(message);
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'An error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (err) {
      // Always show success to prevent email enumeration
      setForgotSent(true);
      toast.success('If an account exists with this email, a reset link has been sent.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password UI
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="forgot-password-page">
        <div className="max-w-md w-full space-y-8 p-8 rounded-2xl feature-card">
          <button
            onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
            className="flex items-center transition-colors text-sunrise-light hover:text-sunrise-dark"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </button>
          
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255, 140, 0, 0.15)' }}>
              <Mail className="w-8 h-8" style={{ color: '#FF8C00' }} />
            </div>
            <h2 className="text-2xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Forgot Password?
            </h2>
            <p className="mt-2 text-sunrise-medium">
              {forgotSent 
                ? "Check your email for the reset link"
                : "Enter your email and we'll send you a reset link"
              }
            </p>
          </div>

          {forgotSent ? (
            <div className="space-y-4">
              <div className="px-4 py-4 rounded-xl flex items-start space-x-3" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', color: '#22C55E' }}>
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Email sent!</p>
                  <p className="text-sm mt-1 opacity-80">
                    If an account exists with {forgotEmail}, you'll receive a password reset link within a few minutes.
                  </p>
                </div>
              </div>
              <p className="text-sm text-center text-sunrise-light">
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => setForgotSent(false)}
                  className="hover:underline"
                  style={{ color: '#FF8C00' }}
                >
                  try again
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-sunrise-black">Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-xl input-sunrise"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full py-4 rounded-full font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed btn-primary-cta"
                data-testid="forgot-submit-button"
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="auth-page">
      <div className="max-w-md w-full space-y-8 p-8 rounded-2xl feature-card">
        <div>
          <h2 className="text-3xl font-bold text-center text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="mt-2 text-center text-sunrise-medium">
            {isLogin ? 'Sign in to continue your wellness journey' : 'Start your mental health journey today'}
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-xl flex items-center space-x-2" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#EF4444' }} data-testid="auth-error">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="auth-form">
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2 text-sunrise-black">Full Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl input-sunrise"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  data-testid="auth-fullname-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-sunrise-black">Age</label>
                <input
                  type="number"
                  required
                  min="18"
                  className="w-full px-4 py-3 rounded-xl input-sunrise"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  data-testid="auth-age-input"
                />
                <p className="text-xs mt-1 text-sunrise-light">You must be 18 or older to use this service</p>
              </div>

              {/* User Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-3 text-sunrise-black">Who are you signing up as?</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, user_type: 'normal_user' })}
                    className="flex flex-col items-center p-4 rounded-xl border-2 transition-all"
                    style={{
                      borderColor: formData.user_type === 'normal_user' ? '#FF8C00' : 'var(--card-border)',
                      background: formData.user_type === 'normal_user' ? 'rgba(255, 140, 0, 0.1)' : 'transparent'
                    }}
                    data-testid="user-type-individual"
                  >
                    <User className="w-8 h-8 mb-2" style={{ color: formData.user_type === 'normal_user' ? '#FF8C00' : 'var(--text-light)' }} />
                    <span className="font-medium text-sunrise-black">Individual User</span>
                    <span className="text-xs mt-1 text-sunrise-light">Track your wellness</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, user_type: 'doctor' })}
                    className="flex flex-col items-center p-4 rounded-xl border-2 transition-all"
                    style={{
                      borderColor: formData.user_type === 'doctor' ? '#06B6D4' : 'var(--card-border)',
                      background: formData.user_type === 'doctor' ? 'rgba(6, 182, 212, 0.1)' : 'transparent'
                    }}
                    data-testid="user-type-doctor"
                  >
                    <Stethoscope className="w-8 h-8 mb-2" style={{ color: formData.user_type === 'doctor' ? '#06B6D4' : 'var(--text-light)' }} />
                    <span className="font-medium text-sunrise-black">Doctor</span>
                    <span className="text-xs mt-1 text-sunrise-light">Monitor patients</span>
                  </button>
                </div>
              </div>

              {/* Doctor-specific fields */}
              {formData.user_type === 'doctor' && (
                <div className="space-y-4 p-4 rounded-xl" style={{ background: 'rgba(6, 182, 212, 0.05)', border: '1px solid rgba(6, 182, 212, 0.2)' }}>
                  <p className="text-sm font-medium" style={{ color: '#06B6D4' }}>Doctor Information</p>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-sunrise-black">Medical License Number</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., MCI-12345"
                      className="w-full px-4 py-3 rounded-xl input-sunrise"
                      value={formData.medical_license}
                      onChange={(e) => setFormData({ ...formData, medical_license: e.target.value })}
                      data-testid="doctor-license-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-sunrise-black">Specialization</label>
                    <select
                      required
                      className="w-full px-4 py-3 rounded-xl input-sunrise"
                      value={formData.specialization}
                      onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                      data-testid="doctor-specialization-input"
                    >
                      <option value="">Select specialization</option>
                      <option value="psychiatry">Psychiatry</option>
                      <option value="psychology">Psychology</option>
                      <option value="general_practice">General Practice</option>
                      <option value="neurology">Neurology</option>
                      <option value="counseling">Counseling</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-sunrise-black">Hospital/Clinic Affiliation</label>
                    <input
                      type="text"
                      placeholder="e.g., City General Hospital"
                      className="w-full px-4 py-3 rounded-xl input-sunrise"
                      value={formData.hospital_affiliation}
                      onChange={(e) => setFormData({ ...formData, hospital_affiliation: e.target.value })}
                      data-testid="doctor-hospital-input"
                    />
                  </div>
                  <p className="text-xs text-sunrise-light">
                    Note: Your account will be reviewed by our admin team before approval.
                  </p>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-2 text-sunrise-black">Email</label>
            <input
              type="email"
              required
              className="w-full px-4 py-3 rounded-xl input-sunrise"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="auth-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-sunrise-black">Password</label>
            <input
              type="password"
              required
              className="w-full px-4 py-3 rounded-xl input-sunrise"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              data-testid="auth-password-input"
            />
            {isLogin && (
              <button
                type="button"
                onClick={() => { setShowForgotPassword(true); setForgotEmail(formData.email); }}
                className="text-sm hover:underline mt-2 block"
                style={{ color: '#FF8C00' }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            )}
          </div>

          {!isLogin && (
            <div className="space-y-3 p-4 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
              <p className="text-sm font-semibold text-sunrise-black">Required Consents:</p>
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={formData.consent_data_usage}
                  onChange={(e) => setFormData({ ...formData, consent_data_usage: e.target.checked })}
                  className="mt-1"
                  data-testid="consent-data-usage"
                />
                <span className="text-sm text-sunrise-dark">I consent to data usage for improving my mental health support</span>
              </label>
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={formData.consent_chat_moderation}
                  onChange={(e) => setFormData({ ...formData, consent_chat_moderation: e.target.checked })}
                  className="mt-1"
                  data-testid="consent-chat-moderation"
                />
                <span className="text-sm text-sunrise-dark">I understand that peer chat conversations may be moderated for safety</span>
              </label>
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={formData.consent_emergency_disclaimer}
                  onChange={(e) => setFormData({ ...formData, consent_emergency_disclaimer: e.target.checked })}
                  className="mt-1"
                  data-testid="consent-emergency-disclaimer"
                />
                <span className="text-sm text-sunrise-dark">I understand this app is not a replacement for professional medical care or emergency services</span>
              </label>
            </div>
          )}

          {/* Referral Code (Registration only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-2 text-sunrise-black">
                <Gift className="w-4 h-4 inline-block mr-1" style={{ color: '#EC4899' }} />
                Referral Code (Optional)
              </label>
              <input
                type="text"
                placeholder="Enter referral code"
                className="w-full px-4 py-3 rounded-xl uppercase input-sunrise"
                style={{
                  borderColor: referralValid === true ? '#22C55E' : referralValid === false ? '#EF4444' : undefined
                }}
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                data-testid="referral-code-input"
              />
              {referralMessage && (
                <p className="text-xs mt-1" style={{ color: referralValid ? '#22C55E' : '#EF4444' }}>
                  {referralMessage}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-full font-semibold btn-primary-cta hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="auth-submit-button"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="hover:underline"
            style={{ color: '#FF8C00' }}
            data-testid="auth-toggle-mode"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};